/**
 * Type definitions for the Shadow AI Discovery feature.
 *
 * Mirrors the API contract under `/api/v1/aws-discovery`. The BACKEND schemas
 * (`src/app/schemas/aws_discovery.py`) are the source of truth — these types are
 * kept in lockstep with them. Every string field on an asset (name,
 * arn_or_native_id, asset_metadata values, posture-flag codes) is derived from a
 * customer's AWS account and must be treated as untrusted input. Never render
 * these with `dangerouslySetInnerHTML` — render as text only.
 */

// --- Setup / connect ---

/** Mirror of backend `AwsDiscoverySetupInstructions`. `instructions` is a single
 * numbered-lines STRING (not an array). */
export interface AwsDiscoverySetupInstructions {
  external_id: string;
  our_account_id: string;
  cloudformation_template: string;
  console_quick_create_url: string | null;
  suggested_regions: string[];
  instructions: string;
}

/** Mirror of backend `AwsDiscoveryConnectionCreate` (extra="forbid"). */
export interface AwsDiscoveryConnectionCreate {
  name: string;
  role_arn: string;
  external_id: string;
  regions: string[];
}

/** Mirror of backend `AwsDiscoveryConnectionRead`. Secret material is never exposed. */
export interface AwsDiscoveryConnection {
  id: number;
  organization_id: number;
  name: string;
  aws_account_id: string | null;
  account_id_conflict: boolean;
  regions: string[];
  is_active: boolean;
  last_scan_at: string | null;
  last_error: string | null;
  consecutive_errors: number;
  created_at: string;
  updated_at: string | null;
}

// --- Scans ---

export type ScanStatus = "queued" | "running" | "completed" | "partial" | "failed";

export const TERMINAL_SCAN_STATUSES: ScanStatus[] = ["completed", "partial", "failed"];

export function isTerminalScanStatus(status: ScanStatus | undefined): boolean {
  return status !== undefined && TERMINAL_SCAN_STATUSES.includes(status);
}

/** Mirror of backend `DiscoveryScanRead`. Returned by both trigger (202) and poll. */
export interface DiscoveryScan {
  id: number;
  connection_id: number;
  organization_id: number;
  status: ScanStatus;
  started_at: string | null;
  finished_at: string | null;
  // Per-(service:region) outcome map, e.g. {"sagemaker:eu-west-1": "access_denied"}.
  error_detail: Record<string, string>;
  assets_found: number;
  assets_active: number;
  created_at: string;
}

// --- Assets ---

/** Backend `DiscoveryAssetType` values (lowercase, string-backed enum). */
export type AssetType =
  | "bedrock_model_access"
  | "bedrock_custom_model"
  | "bedrock_agent"
  | "bedrock_knowledge_base"
  | "bedrock_guardrail"
  | "sagemaker_endpoint"
  | "sagemaker_model"
  | "sagemaker_notebook"
  | "heuristic_secret"
  | "heuristic_ssm_parameter"
  | "heuristic_lambda";

/** Severity buckets the UI derives from a posture-flag code (backend stores codes only). */
export type PostureSeverity = "critical" | "high" | "medium" | "low" | "info";

/** Mirror of backend `DiscoveredAiAssetRead`. `posture_flags` is a list of short
 * machine-string CODES (e.g. "no_kms_encryption"); the UI maps each to a severity
 * and label. `asset_metadata` is an untrusted free-form dict. */
export interface DiscoveredAsset {
  id: number;
  organization_id: number;
  connection_id: number;
  asset_type: AssetType;
  region: string;
  arn_or_native_id: string;
  name: string;
  asset_metadata: Record<string, unknown>;
  posture_flags: string[];
  first_seen_at: string;
  last_seen_at: string;
  is_active: boolean;
}

export type ScanCapabilityReason =
  | "ok"
  | "tier_upgrade_required"
  | "unsupported_asset_type";

/** Mirror of backend `ScanCapability`. */
export interface ScanCapability {
  eligible: boolean;
  reason: ScanCapabilityReason;
}

/** Mirror of backend `DiscoveredAiAssetDetailRead`. */
export interface DiscoveredAssetDetail extends DiscoveredAsset {
  scan_capability: ScanCapability;
}

// --- Filters / pagination ---

export interface AssetFilters {
  asset_type?: AssetType;
  region?: string;
  connection_id?: number;
  has_posture_flags?: boolean;
  page?: number;
  items_per_page?: number;
}

/** Mirror of fastcrud `PaginatedListResponse` (note: no `total_pages`). */
export interface PaginatedResponse<T> {
  data: T[];
  total_count: number;
  has_more: boolean;
  page: number;
  items_per_page: number;
}

export type PaginatedAssets = PaginatedResponse<DiscoveredAsset>;
export type PaginatedConnections = PaginatedResponse<AwsDiscoveryConnection>;

// --- Display helpers ---

/** Human-readable labels for asset types. Used for table grouping and filters. */
export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  bedrock_model_access: "Bedrock Model Access",
  bedrock_custom_model: "Bedrock Custom Model",
  bedrock_agent: "Bedrock Agent",
  bedrock_knowledge_base: "Bedrock Knowledge Base",
  bedrock_guardrail: "Bedrock Guardrail",
  sagemaker_endpoint: "SageMaker Endpoint",
  sagemaker_model: "SageMaker Model",
  sagemaker_notebook: "SageMaker Notebook",
  heuristic_secret: "Secret (AI key signal)",
  heuristic_ssm_parameter: "SSM Param (AI signal)",
  heuristic_lambda: "Lambda (AI SDK signal)",
};

/**
 * Grouping order for the inventory table. First-class AWS AI services, then
 * heuristic signals last.
 */
export const ASSET_TYPE_ORDER: AssetType[] = [
  "sagemaker_endpoint",
  "sagemaker_model",
  "sagemaker_notebook",
  "bedrock_model_access",
  "bedrock_custom_model",
  "bedrock_agent",
  "bedrock_knowledge_base",
  "bedrock_guardrail",
  "heuristic_secret",
  "heuristic_ssm_parameter",
  "heuristic_lambda",
];

/**
 * Map a backend posture-flag CODE to a UI severity + human label. The backend stores
 * only the code string (see `src/app/core/worker/aws_discovery/posture.py`); copy and
 * severity live here on the frontend. Unknown codes fall back to `info` and a
 * humanized version of the code.
 */
const POSTURE_FLAG_META: Record<string, { severity: PostureSeverity; label: string }> = {
  direct_internet_access: { severity: "critical", label: "Direct internet access" },
  no_network_isolation: { severity: "high", label: "No network isolation" },
  no_vpc_config: { severity: "high", label: "No VPC config" },
  invocation_logging_disabled: { severity: "high", label: "Invocation logging disabled" },
  no_kms_encryption: { severity: "medium", label: "No KMS encryption" },
  no_data_capture: { severity: "medium", label: "No data capture" },
  no_production_variants: { severity: "low", label: "No production variants" },
  ai_env_var_present: { severity: "info", label: "AI env var present" },
  secret_value_not_inspected: { severity: "info", label: "Secret value not inspected" },
};

function humanizeCode(code: string): string {
  return code.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function postureFlagSeverity(code: string): PostureSeverity {
  return POSTURE_FLAG_META[code]?.severity ?? "info";
}

export function postureFlagLabel(code: string): string {
  return POSTURE_FLAG_META[code]?.label ?? humanizeCode(code);
}
